"use client";

import { c as _c } from "react/compiler-runtime";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import type { VariantProps } from "class-variance-authority";
import { cn } from "cn";
import * as React from "react";
import { toggleVariants } from "./Toggle";
type ToggleGroupStyle = VariantProps<typeof toggleVariants> & {
  /**
   * Gap between items, as a multiple of Tailwind's `--spacing`.
   * `0` joins the items into a single segmented control.
   */
  spacing?: number;
  orientation?: "horizontal" | "vertical";
};
const ToggleGroupContext = React.createContext<ToggleGroupStyle>({
  size: "default",
  variant: "ghost",
  roundness: "default",
  spacing: 2,
  orientation: "horizontal"
});
type ToggleGroupProps = React.ComponentProps<typeof ToggleGroupPrimitive.Root> & ToggleGroupStyle;

/**
 * A set of toggles where one (`type="single"`) or several
 * (`type="multiple"`) can be on. A single group can be cleared by pressing
 * the item that is on.
 */
function ToggleGroup(t0) {
  const $ = _c(31);
  let children;
  let className;
  let props;
  let roundness;
  let size;
  let t1;
  let t2;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      roundness,
      spacing: t1,
      orientation: t2,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = roundness;
    $[5] = size;
    $[6] = t1;
    $[7] = t2;
    $[8] = variant;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    roundness = $[4];
    size = $[5];
    t1 = $[6];
    t2 = $[7];
    variant = $[8];
  }
  const spacing = t1 === undefined ? 2 : t1;
  const orientation = t2 === undefined ? "horizontal" : t2;
  let t3;
  if ($[9] !== spacing) {
    t3 = {
      "--gap": spacing
    };
    $[9] = spacing;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = t3 as React.CSSProperties;
  let t5;
  if ($[11] !== className) {
    t5 = cn("group/toggle-group flex w-fit flex-row items-center gap-[--spacing(var(--gap))] not-data-[spacing=0]:flex-wrap data-vertical:flex-col data-vertical:items-stretch", className);
    $[11] = className;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== orientation || $[14] !== roundness || $[15] !== size || $[16] !== spacing || $[17] !== variant) {
    t6 = {
      variant,
      size,
      roundness,
      spacing,
      orientation
    };
    $[13] = orientation;
    $[14] = roundness;
    $[15] = size;
    $[16] = spacing;
    $[17] = variant;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== children || $[20] !== t6) {
    t7 = <ToggleGroupContext.Provider value={t6}>{children}</ToggleGroupContext.Provider>;
    $[19] = children;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== orientation || $[23] !== props || $[24] !== size || $[25] !== spacing || $[26] !== t4 || $[27] !== t5 || $[28] !== t7 || $[29] !== variant) {
    t8 = <ToggleGroupPrimitive.Root data-slot="toggle-group" data-variant={variant} data-size={size} data-spacing={spacing} data-orientation={orientation} orientation={orientation} style={t4} className={t5} {...props}>{t7}</ToggleGroupPrimitive.Root>;
    $[22] = orientation;
    $[23] = props;
    $[24] = size;
    $[25] = spacing;
    $[26] = t4;
    $[27] = t5;
    $[28] = t7;
    $[29] = variant;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
}
type ToggleGroupItemProps = React.ComponentProps<typeof ToggleGroupPrimitive.Item> & VariantProps<typeof toggleVariants>;
function ToggleGroupItem(t0) {
  const $ = _c(22);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      className,
      children,
      variant: t1,
      size: t2,
      roundness: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const variant = t1 === undefined ? "ghost" : t1;
  const size = t2 === undefined ? "default" : t2;
  const roundness = t3 === undefined ? "default" : t3;
  const context = React.useContext(ToggleGroupContext);
  const t4 = context.variant || variant;
  const t5 = context.size || size;
  const t6 = context.spacing;
  let t7;
  if ($[7] !== className || $[8] !== context.roundness || $[9] !== context.size || $[10] !== context.variant || $[11] !== roundness || $[12] !== size || $[13] !== variant) {
    t7 = cn("shrink-0 focus:z-10 focus-visible:z-10 data-[state=on]:z-10 group-data-horizontal/toggle-group:data-[spacing=0]:not-first:rounded-l-none group-data-horizontal/toggle-group:data-[spacing=0]:not-last:rounded-r-none group-data-vertical/toggle-group:data-[spacing=0]:not-first:rounded-t-none group-data-vertical/toggle-group:data-[spacing=0]:not-last:rounded-b-none group-data-horizontal/toggle-group:data-[spacing=0]:data-[variant=outline]:not-first:-ml-px group-data-vertical/toggle-group:data-[spacing=0]:data-[variant=outline]:not-first:-mt-px", toggleVariants({
      variant: context.variant || variant,
      size: context.size || size,
      roundness: context.roundness || roundness
    }), className);
    $[7] = className;
    $[8] = context.roundness;
    $[9] = context.size;
    $[10] = context.variant;
    $[11] = roundness;
    $[12] = size;
    $[13] = variant;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== children || $[16] !== context.spacing || $[17] !== props || $[18] !== t4 || $[19] !== t5 || $[20] !== t7) {
    t8 = <ToggleGroupPrimitive.Item data-slot="toggle-group-item" data-variant={t4} data-size={t5} data-spacing={t6} className={t7} {...props}>{children}</ToggleGroupPrimitive.Item>;
    $[15] = children;
    $[16] = context.spacing;
    $[17] = props;
    $[18] = t4;
    $[19] = t5;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}
export { ToggleGroup, ToggleGroupItem, type ToggleGroupItemProps, type ToggleGroupProps };